// src/Compliance.js

import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Text,
  Button,
  Drawer,
  Divider,
  Table,
  Tooltip,
  Collapse,
  Badge,
  ActionIcon,
  Group,
  Notification,
} from '@mantine/core';
import {
  IconPlus,
  IconPlaylistAdd,
  IconPencil,
  IconX,
  IconAdjustments,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';

import { fetchBaselineControls, saveBaselineControl  } from './redux/actions/complianceActions'; 
import IconWithProgress from './IconWithProgress';
import { useAuth0 } from '@auth0/auth0-react';
import useSaveOperation from './useSaveOperation';
import AutosaveIndicator from './AutoSaveIndicator'; 
import BaselineControlEvaluationForm from './BaselineControlEvaluation'

/**
 * Aggregates all saved risks from the provided riskFormData.
 *
 * @param {Object} riskFormData - The comprehensive risk form data containing multiple sections.
 * @returns {Array} - An array of saved risk objects with their respective section and risk source.
 */
function aggregateRisks(riskFormData) {
  const aggregatedRisks = [];

  // Iterate over each section in riskFormData
  Object.entries(riskFormData).forEach(([sectionName, sectionData]) => {
    const { riskSources, sectionRisks } = sectionData;

    // Ensure both riskSources and sectionRisks exist
    if (riskSources && sectionRisks) {
      riskSources.forEach((riskSource) => {
        const { name: riskSourceName } = riskSource;

        // Retrieve risks associated with the current risk source
        const risks = sectionRisks[riskSourceName] || [];

        risks.forEach((risk) => {
          const {
            riskId,
            riskName,
            category,
            type,
            sourceFile,
            causes,
            status,
            owner,
          } = risk;

          // **Filter out risks that are not "Saved"**
          if (status !== 'Saved') return;

          // Construct the aggregated risk object
          const aggregatedRisk = {
            riskId: riskId || null, // Handle cases where riskId might be undefined
            riskName,
            category,
            type,
            sourceFile,
            causes,
            status,
            owner: owner || null, // Handle cases where owner might be undefined
            section: sectionName,
            riskSource: riskSourceName,
          };

          aggregatedRisks.push(aggregatedRisk);
        });
      });
    }
  });

  return aggregatedRisks;
}

/**
 * Converts a string to Title Case.
 *
 * @param {string} str - The string to convert.
 * @returns {string} - The Title Cased string.
 */
const toTitleCase = (str) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Compliance Component
 *
 * @param {Object} props
 * @param {string} props.modelId - The ID of the model.
 * @param {string} props.assessmentUuid - The UUID of the assessment.
 * @param {Object} props.riskFormData - The entire risk form data containing multiple sections.
 * @param {Object} props.narratives - The narratives associated with each section.
 */
function Compliance({ modelId, assessmentUuid, riskFormData, narratives }) {
  const dispatch = useDispatch();
  const { baselineControls, loading, error } = useSelector((state) => state.compliance);
  const { user } = useAuth0();
  const { isSaving, isSaved, handleSaveOperation } = useSaveOperation();

  // Snackbar State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Aggregated Risks
  const aggregatedRisks = useMemo(() => aggregateRisks(riskFormData), [riskFormData]);

    // --- Evaluation Drawer State for Baseline Controls ---
    const [evaluationDrawerOpen, setEvaluationDrawerOpen] = useState(false);
    const [selectedBaselineControlId, setSelectedBaselineControlId] = useState(null);
 
 //State for policy expand
    const [expandedPolicyDomain, setExpandedPolicyDomain] = useState(null);
    const [expandedRisks, setExpandedRisks] = useState({});   /**
   * Handler to generate baseline controls.
   * This function aggregates risks and dispatches the action to fetch baseline controls.
   */
  const handleGenerateControls = async () => {
    if (modelId && aggregatedRisks.length > 0 && narratives) {
      try {
        await dispatch(fetchBaselineControls(modelId, assessmentUuid, aggregatedRisks, narratives));
        setSnackbarMessage('Baseline controls fetched successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (err) {
        setSnackbarMessage('Failed to fetch baseline controls.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage('Insufficient data to generate baseline controls.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  /**
   * Groups controls by their associated policy domain and risk.
   *
   * @returns {Object} - An object where each key is a policy domain and the value is an object of risks with their controls.
   */
  const groupedData = useMemo(() => {
    const groups = {};

    baselineControls.forEach((control) => {
      const policyDomain = control.namespace || 'General'; // Default to 'General' if namespace is undefined
      const riskId = control.riskId;
      const risk = aggregatedRisks.find((r) => r.riskId === riskId);

      if (!risk) return; // Skip if risk not found

      if (!groups[policyDomain]) {
        groups[policyDomain] = {};
      }

      if (!groups[policyDomain][riskId]) {
        groups[policyDomain][riskId] = {
          riskName: risk.riskName,
          controls: [],
        };
      }

      groups[policyDomain][riskId].controls.push(control);
    });

    return groups;
  }, [baselineControls, aggregatedRisks]);

  const handleAddToBaseline = async (control) => {
    // We wrap the actual saving function with handleSaveOperation from useSaveOperation
    await handleSaveOperation(async () => {
      // Build data to pass to Netlify function
      const payload = {
        controlUuid: control.controlUuid,
        riskId: control.riskId,
        modelUuid: modelId,
        assessmentUuid,
        controlName: control.control,
        createdBy: user.email,   // pass user info
      };
      // Dispatch the thunk
      await dispatch(saveBaselineControl(payload));
    });
    // Optionally show a separate success if needed
    setSnackbarMessage('Added to baseline!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleOpenEvaluation = (controlId) => {
    setSelectedBaselineControlId(controlId);
    setEvaluationDrawerOpen(true);
  };

  const handleCloseEvaluation = () => {
    setEvaluationDrawerOpen(false);
    setSelectedBaselineControlId(null);
  };

  // State to manage which policy domain row is expanded

  const toggleExpandPolicyDomain = (policyDomain) => {
    setExpandedPolicyDomain((prev) => (prev === policyDomain ? null : policyDomain));
  };

  // State to manage expanded Risks


  const toggleExpandRisk = (riskId) => {
    setExpandedRisks((prev) => ({
      ...prev,
      [riskId]: !prev[riskId],
    }));
  };
  return (
    <Box style={{ padding: 16 }}>
      {/* Header */}
      <Paper withBorder radius="md" p="md" style={{ marginBottom: 16 }}>
        <Text size="lg" fw={600}>Compliance Assessment</Text>
        <Text size="sm" c="dimmed" style={{ marginTop: 4 }}>
          Risk-based gap analysis against policies and procedures.
        </Text>
        <Group style={{ marginTop: 12 }} align="center">
          <Button
            variant="outline"
            size="compact-sm"
            onClick={handleGenerateControls}
            disabled={loading}
            leftSection={<IconWithProgress isLoading={loading} />}
          >
            {loading ? 'Generating...' : 'Generate Compliance Report'}
          </Button>
          <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
        </Group>
      </Paper>

      <Paper withBorder radius="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Text fw={600} size="sm">Policy Domain</Text>
              </Table.Th>
              <Table.Th>
                <Text fw={600} size="sm">Affected Risks</Text>
              </Table.Th>
              <Table.Th>
                <Text fw={600} size="sm">Total Gaps</Text>
              </Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>
                <Text fw={600} size="sm">Actions</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Object.entries(groupedData).map(([policyDomain, risks]) => {
              const numberOfRisks = Object.keys(risks).length;
              const totalGaps = Object.values(risks).reduce((acc, r) => acc + r.controls.length, 0);

              return (
                <React.Fragment key={policyDomain}>
                  <Table.Tr>
                    <Table.Td>
                      <Text>{toTitleCase(policyDomain)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="blue" variant="light">{numberOfRisks}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="grape" variant="light">{totalGaps}</Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Tooltip label={expandedPolicyDomain === policyDomain ? 'Collapse Risks' : 'View Risks'}>
                        <ActionIcon variant="subtle" onClick={() => toggleExpandPolicyDomain(policyDomain)}>
                          {expandedPolicyDomain === policyDomain ? (
                            <IconChevronDown size={18} />
                          ) : (
                            <IconChevronRight size={18} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>

                  <Table.Tr>
                    <Table.Td colSpan={4} style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={expandedPolicyDomain === policyDomain}>
                        <Box style={{ margin: 8 }}>
                          {Object.keys(risks).length > 0 ? (
                            <Table>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ paddingLeft: 24 }}>
                                    <Text fw={600} size="sm">Risk Name</Text>
                                  </Table.Th>
                                  <Table.Th>
                                    <Text fw={600} size="sm">Identified Gaps</Text>
                                  </Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>
                                    <Text fw={600} size="sm">Actions</Text>
                                  </Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {Object.entries(risks).map(([riskId, { riskName, controls }]) => {
                                  const identifiedGaps = controls.length;
                                  return (
                                    <React.Fragment key={riskId}>
                                      <Table.Tr>
                                        <Table.Td style={{ paddingLeft: 32 }}>
                                          <Text>{riskName}</Text>
                                        </Table.Td>
                                        <Table.Td>
                                          <Badge color="yellow" variant="light">{identifiedGaps}</Badge>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'right' }}>
                                          <Tooltip label={expandedRisks[riskId] ? 'Collapse Controls' : 'View Controls'}>
                                            <ActionIcon variant="subtle" onClick={() => toggleExpandRisk(riskId)}>
                                              {expandedRisks[riskId] ? (
                                                <IconChevronDown size={18} />
                                              ) : (
                                                <IconChevronRight size={18} />
                                              )}
                                            </ActionIcon>
                                          </Tooltip>
                                        </Table.Td>
                                      </Table.Tr>

                                      <Table.Tr>
                                        <Table.Td colSpan={3} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                          <Collapse in={expandedRisks[riskId]}>
                                            <Box style={{ margin: 8 }}>
                                              {controls && controls.length > 0 ? (
                                                <Table>
                                                  <Table.Thead>
                                                    <Table.Tr>
                                                      <Table.Th style={{ paddingLeft: 40 }}>
                                                        <Text fw={600} size="sm">Control Name</Text>
                                                      </Table.Th>
                                                      <Table.Th>
                                                        <Text fw={600} size="sm">Actions</Text>
                                                      </Table.Th>
                                                    </Table.Tr>
                                                  </Table.Thead>
                                                  <Table.Tbody>
                                                    {controls.map((control) => (
                                                      <Table.Tr key={control.controlUuid || control.controlId}>
                                                        <Table.Td style={{ paddingLeft: 48 }}>
                                                          <Text title={control.control} truncate>
                                                            {control.control}
                                                          </Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6}>
                                                            <Tooltip label="Add to Action Plan">
                                                              <ActionIcon onClick={() => handleAddToBaseline(control)} variant="light">
                                                                <IconPlus size={16} />
                                                              </ActionIcon>
                                                            </Tooltip>
                                                            <Tooltip label="Add to Baseline">
                                                              <ActionIcon onClick={() => handleAddToBaseline(control)} variant="light">
                                                                <IconPlaylistAdd size={16} />
                                                              </ActionIcon>
                                                            </Tooltip>
                                                            <Tooltip label="Edit Control">
                                                              <ActionIcon onClick={() => {}} variant="light">
                                                                <IconPencil size={16} />
                                                              </ActionIcon>
                                                            </Tooltip>
                                                            <Tooltip label="Evaluate Baseline Control">
                                                              <ActionIcon onClick={() => handleOpenEvaluation(control.controlUuid)} variant="light">
                                                                <IconAdjustments size={16} />
                                                              </ActionIcon>
                                                            </Tooltip>
                                                          </Group>
                                                        </Table.Td>
                                                      </Table.Tr>
                                                    ))}
                                                  </Table.Tbody>
                                                </Table>
                                              ) : (
                                                <Text c="dimmed">No controls available for this risk.</Text>
                                              )}
                                            </Box>
                                          </Collapse>
                                        </Table.Td>
                                      </Table.Tr>
                                    </React.Fragment>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          ) : (
                            <Text c="dimmed">No risks available for this policy domain.</Text>
                          )}
                        </Box>
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                </React.Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {baselineControls.length === 0 && !loading && !error && (
        <Box style={{ marginTop: 8 }}>
          <Text>No baseline compliance generated yet.</Text>
        </Box>
      )}

      {error && (
        <Box style={{ marginTop: 8 }}>
          <Text c="red">{error}</Text>
        </Box>
      )}

      <Drawer
        opened={evaluationDrawerOpen}
        onClose={handleCloseEvaluation}
        position="right"
        size="50%"
        title={<Text fw={600}>Baseline Control Evaluation</Text>}
        withCloseButton
      >
        <Divider my="sm" />
        <Box style={{ flexGrow: 1, overflowY: 'auto', marginTop: 8 }}>
          {selectedBaselineControlId && (
            <BaselineControlEvaluationForm
              baselineControlId={selectedBaselineControlId}
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              handleSaveOperation={handleSaveOperation}
            />
          )}
        </Box>
        <Group justify="flex-end" mt="md">
          <Button variant="filled" onClick={handleCloseEvaluation} leftSection={<IconX size={16} />}>Close</Button>
        </Group>
      </Drawer>

      {snackbarOpen && (
        <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          <Notification
            withBorder
            withCloseButton
            color={snackbarSeverity === 'error' ? 'red' : snackbarSeverity === 'warning' ? 'yellow' : 'green'}
            onClose={handleSnackbarClose}
          >
            {snackbarMessage}
          </Notification>
        </div>
      )}
    </Box>
  );
}

export default Compliance;
